import type { PairingFilter, Supplier } from '@/common/db-typebox';
import type { WineProductItem } from '@/common/domain-model/product-search';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { useTreatyClient } from '@/lib/client';
import { ReloadOutlined } from '@ant-design/icons';
import { Button, Input, Space, message } from 'antd';
import { debounce } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { ActionView } from './components/ActionView';
import { AddWineOrEditWineModal } from './components/AddWineOrEditWineModal';
import { DetailModalContent } from './components/DetailModalContent/DetailModalContent';
import { FilterCard, type WineFilter } from './components/FilterCard';
import { TableView } from './components/TableView';

export const WineListPage = observer(() => {
  const { priceRange } = useCurrencySymbol();
  const client = useTreatyClient();

  const defaultFilter: WineFilter = {
    priceRange: { min: priceRange.min, max: priceRange.max },
    wineTypes: [],
    regions: [],
    suppliers: [],
    status: [],
  };

  const [searchValue, setSearchValue] = useState('');
  const [filter, setFilter] = useState<WineFilter>(defaultFilter);
  const [addOrEditModalOpen, setAddOrEditModalOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState<
    WineProductItem | undefined
  >();
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<WineProductItem[]>([]);
  const [total, setTotal] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const [supplierList, setSupplierList] = useState<Supplier[]>([]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const fetchSupplierList = async () => {
      const response = await client.api.supplier.list.get();
      setSupplierList(response.data?.data.suppliers || []);
    };
    fetchSupplierList();
  }, []);

  // Fetch data using API
  const fetchData = useCallback(
    debounce(
      async (
        filterParams: WineFilter,
        paginationParams: { current: number; pageSize: number },
        searchTerm: string,
        currencyPriceRangeLimit: { min: number; max: number }, // currency price range
      ) => {
        setLoading(true);
        try {
          const min =
            filterParams.priceRange.min ?? currencyPriceRangeLimit.min;
          const max =
            filterParams.priceRange.max ?? currencyPriceRangeLimit.max;

          // Build API query parameters
          const pairingFilter: Omit<PairingFilter, 'limit'> = {
            suppliers: [],
            status: [],
            sources: [],
          };

          // Add optional parameters
          if (
            min > currencyPriceRangeLimit.min ||
            max < currencyPriceRangeLimit.max
          ) {
            pairingFilter.price_range = {};
            if (min > currencyPriceRangeLimit.min)
              pairingFilter.price_range.min = min;
            if (max < currencyPriceRangeLimit.max)
              pairingFilter.price_range.max = max;
          }

          if (filterParams.wineTypes.length > 0) {
            pairingFilter.wine_types = filterParams.wineTypes;
          }
          if (filterParams.regions.length > 0) {
            pairingFilter.countries = filterParams.regions;
          }
          if (filterParams.status.length > 0) {
            pairingFilter.status = filterParams.status;
          }
          if (filterParams.suppliers.length > 0) {
            pairingFilter.suppliers = filterParams.suppliers;
          }
          if (filterParams.regions.length > 0) {
            pairingFilter.countries = filterParams.regions;
          }

          // Call API
          const response = await client.api.wine_product.list.get({
            query: {
              title: searchTerm,
              filter: pairingFilter,
              page: paginationParams.current,
              limit: paginationParams.pageSize,
            },
          });

          if (response.data) {
            setProducts(response.data.data.products);
            setTotal(response.data.data.total);
          }
        } catch (error) {
          console.error('Failed to fetch product list', error);
        } finally {
          setLoading(false);
        }
      },
      500,
    ),
    [],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    fetchData(filter, pagination, searchValue, priceRange);
  }, [filter, searchValue, pagination, priceRange]);

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPagination({
      current: page,
      pageSize: pageSize,
    });
  };

  // Add refresh function
  const refreshData = useCallback(() => {
    fetchData(filter, pagination, searchValue, priceRange);
  }, [filter, pagination, searchValue, priceRange, fetchData]);

  const handleDelete = async (ids: string[]) => {
    try {
      const res = await client.api.wine_product.delete({
        ids,
      });
      if (res.data?.success) {
        if (ids.length === 1) {
          message.success('Wine deleted successfully');
        } else {
          message.success(`${ids.length} wines deleted successfully`);
        }
        setSelectedRowKeys([]);
        refreshData();
      } else {
        message.error(res.error?.value.message || 'Failed to delete wine(s)');
      }
    } catch (error) {
      console.error(error);
      message.error(`Failed to delete wine(s): ${error}`);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-8 overflow-auto h-full">
      <div className="flex justify-end gap-4">
        <ActionView
          selectedRowKeys={selectedRowKeys}
          onAddWine={() => setAddOrEditModalOpen(true)}
          onDelete={handleDelete}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-1 space-y-4">
          <Space>
            <Input.Search
              onSearch={(value) => setSearchValue(value)}
              allowClear
              placeholder="Search wines..."
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={async () => {
                refreshData();
              }}
              loading={loading}
            />
          </Space>
          <FilterCard
            filter={filter}
            suppliersData={supplierList}
            onChange={setFilter}
            onClear={() => setFilter(defaultFilter)}
          />
        </div>
        <div className="md:col-span-3">
          <TableView
            records={products}
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: total,
              onChange: handlePaginationChange,
            }}
            onViewDetail={(product) => {
              setCurrentProduct(product);
              setOpenDetailModal(true);
            }}
            onEdit={(product) => {
              setCurrentProduct(product);
              setAddOrEditModalOpen(true);
            }}
            selectedRowKeys={selectedRowKeys}
            onSelectedRowKeysChange={setSelectedRowKeys}
            onDelete={(record) => handleDelete([record.id])}
          />
        </div>
      </div>
      <AddWineOrEditWineModal
        open={addOrEditModalOpen}
        onClose={() => {
          setAddOrEditModalOpen(false);
          setCurrentProduct(undefined);
        }}
        onSuccess={refreshData}
        currentProduct={currentProduct}
      />
      <DetailModalContent
        productItem={currentProduct}
        open={openDetailModal}
        onClose={() => {
          setOpenDetailModal(false);
          setCurrentProduct(undefined);
        }}
        onEdit={() => {
          setCurrentProduct(currentProduct);
          setOpenDetailModal(false);
          setAddOrEditModalOpen(true);
        }}
      />
    </div>
  );
});
